import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import * as Sentry from '@sentry/angular';

/**
 * Route target for the OIDC redirect URI (`/auth/callback`).
 *
 * On a full page reload, `APP_INITIALIZER` already ran `tryLogin()`. On Capacitor
 * (and any soft navigation that lands here with `?code=`), that initializer may
 * have run *before* the code was in the URL — so we call `tryLogin()` again here
 * (idempotent when tokens are already present) before leaving this screen.
 *
 * Template matches LoginComponent's `completingSignIn` surface so the OIDC
 * reload does not swap to a different loading UI while ProfileResolver waits
 * on /self.
 */
@Component({
  selector: 'auth-callback',
  template: `
    <div class="flex items-center justify-center h-[80vh]">
      <div class="flex flex-col items-center">
        <img src="assets/images/EscrowTabLogo.svg" class="w-auto h-32" alt="" />
        <p class="pt-6 text-2xl font-medium text-center text-midnight">
          Signing you in…
        </p>
        <et-atoms-spinner class="mt-6"></et-atoms-spinner>
      </div>
    </div>
  `,
  standalone: false,
})
export class AuthCallbackComponent implements OnInit {
  private readonly oauth = inject(OAuthService);
  private readonly router = inject(Router);

  async ngOnInit() {
    console.log('[oidc] AuthCallbackComponent.ngOnInit', {
      href: typeof window !== 'undefined' ? window.location.href : undefined,
      loginUrl: this.oauth.loginUrl,
      discoveryDocumentLoaded: this.oauth.discoveryDocumentLoaded,
    });
    try {
      await this.oauth.tryLogin();
      console.log('[oidc] tryLogin resolved on /auth/callback', {
        hasValidAccessToken: this.oauth.hasValidAccessToken(),
        hasValidIdToken: this.oauth.hasValidIdToken(),
        state: this.oauth.state,
      });
    } catch (err) {
      console.error('[oidc] tryLogin failed on /auth/callback', err);
      Sentry.captureException(
        err instanceof Error ? err : new Error(String(err)),
        { tags: { area: 'oidc-callback' } },
      );
    }

    if (!this.oauth.hasValidAccessToken() && !this.oauth.hasValidIdToken()) {
      console.error(
        '[oidc] no valid token after tryLogin on /auth/callback — bouncing to /auth/login?error=oidc_callback',
      );
      void this.router.navigateByUrl('/auth/login?error=oidc_callback', {
        replaceUrl: true,
      });
      return;
    }

    const returnUrl = resolveReturnUrl(this.oauth.state);
    console.log('[oidc] auth/callback complete, navigating', { returnUrl });
    void this.router.navigateByUrl(returnUrl, {
      replaceUrl: true,
    });
  }
}

/**
 * `OAuthService.state` is the user-portion of the OAuth `state` parameter, URL-encoded once
 * (angular-oauth2-oidc only splits on the nonce separator, it does not decodeURIComponent).
 * Reject anything that is not a same-origin path so a tampered `state` cannot navigate away.
 */
export function resolveReturnUrl(rawState: string | undefined | null): string {
  const FALLBACK = '/app';
  if (!rawState) return FALLBACK;
  let decoded: string;
  try {
    decoded = decodeURIComponent(rawState);
  } catch {
    return FALLBACK;
  }
  const trimmed = decoded.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return FALLBACK;
  if (trimmed.startsWith('/auth/')) return FALLBACK;
  return trimmed;
}
